import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  authGetRequest,
  authPostRequest,
  getEffectiveClientId,
} from "../../api/authClient";

type MultiSelectOption = { id: string; name: string };

const getPagedList = (payload: any): any[] => {
  const data = payload?.data ?? payload;
  return (
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(data?.results) && data.results) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload) && payload) ||
    []
  );
};

const getTotalFromPayload = (payload: any): number => {
  const data = payload?.data ?? payload;
  const rawTotal =
    data?.pagination?.total ??
    data?.pagination?.totalCount ??
    data?.meta?.total ??
    data?.total ??
    data?.totalCount ??
    payload?.total ??
    payload?.totalCount ??
    0;
  const n = Number(rawTotal);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getPagingMeta = (
  payload: any,
  fallbackPageSize: number,
  fallbackPage: number
): { page: number; size: number; total: number } => {
  const data = payload?.data ?? payload;
  const total = getTotalFromPayload(payload);
  const rawPage =
    data?.pagination?.page ??
    data?.meta?.page ??
    data?.page ??
    payload?.page ??
    fallbackPage;
  const rawSize =
    data?.pagination?.size ??
    data?.pagination?.pageSize ??
    data?.meta?.size ??
    data?.meta?.pageSize ??
    data?.size ??
    data?.pageSize ??
    payload?.size ??
    payload?.pageSize ??
    fallbackPageSize;
  const page = Math.max(1, Number(rawPage) || fallbackPage);
  const size = Math.max(1, Number(rawSize) || fallbackPageSize);
  return { page, size, total };
};

function CheckboxMultiSelect({
  id,
  label,
  options,
  selectedValues,
  onChange,
  helperText,
  onOpen,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onRetryLoadMore,
  totalCount,
  disabled,
}: {
  id: string;
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (next: string[]) => void;
  helperText?: string;
  onOpen?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: string | null;
  onRetryLoadMore?: () => void;
  totalCount?: number;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const selectedLabels = options
    .filter((opt) => selectedValues.includes(opt.id))
    .map((opt) => opt.name);
  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );
  const filteredOptionIds = filteredOptions.map((opt) => opt.id);
  const selectedFilteredCount = filteredOptionIds.filter((id) => selectedValues.includes(id)).length;
  const hasFilteredOptions = filteredOptionIds.length > 0;
  const allFilteredSelected = hasFilteredOptions && selectedFilteredCount === filteredOptionIds.length;
  const someFilteredSelected = selectedFilteredCount > 0 && !allFilteredSelected;
  const normalizedTotalCount =
    typeof totalCount === "number" && totalCount > 0 ? totalCount : options.length;
  const shownCount = searchTerm.trim() ? filteredOptionIds.length : normalizedTotalCount;

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      onChange(selectedValues.filter((value) => !filteredOptionIds.includes(value)));
      return;
    }
    onChange(Array.from(new Set([...selectedValues, ...filteredOptionIds])));
  };

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;
    const el = e.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 24) onLoadMore();
  };

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  useEffect(() => {
    if (!open || !searchTerm.trim() || !hasMore || isLoadingMore || !onLoadMore) return;
    onLoadMore();
  }, [open, searchTerm, hasMore, isLoadingMore, onLoadMore]);

  return (
    <div ref={containerRef}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label}
      </label>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() =>
          setOpen((p) => {
            if (disabled) return p;
            const nextOpen = !p;
            if (nextOpen) onOpen?.();
            return nextOpen;
          })
        }
        className="w-full rounded-lg bg-white border border-slate-300 text-slate-800 px-3 py-2.5 text-sm text-left focus:outline-none focus:ring-1 focus:ring-blue-500/25 focus:border-blue-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[#0B0F19] dark:border-[#334155] dark:text-slate-100"
      >
        <span className="truncate block">
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : `Select ${label.toLowerCase()}`}
        </span>
      </button>
      {selectedLabels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {options
            .filter((opt) => selectedValues.includes(opt.id))
            .map((opt) => (
              <span
                key={opt.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:border-[#334155] dark:bg-[#0B0F19] dark:text-slate-300"
              >
                {opt.name}
                <button
                  type="button"
                  onClick={() => onChange(selectedValues.filter((v) => v !== opt.id))}
                  className="text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400"
                >
                  x
                </button>
              </span>
            ))}
        </div>
      )}
      {open && (
        <div
          onScroll={handleListScroll}
          className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-300 bg-white dark:bg-[#0B0F19] dark:border-[#334155] px-2 pb-2 pt-0 space-y-2"
        >
          <div className="sticky top-0 z-30 px-0 pt-2 pb-1 bg-white dark:bg-[#0B0F19] border-b border-slate-200 dark:border-[#334155] shadow-sm">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/25 focus:border-blue-500/40 dark:border-[#334155] dark:bg-[#0B0F19] dark:text-slate-200"
            />
            <div className="mt-1 flex items-center justify-between px-0.5">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">{shownCount} shown</span>
            </div>
            {hasFilteredOptions ? (
              <label className="mt-1 inline-flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someFilteredSelected;
                  }}
                  onChange={toggleSelectAllFiltered}
                  className="h-4 w-4"
                />
                <span>{allFilteredSelected ? "Clear all shown" : "Select all shown"}</span>
              </label>
            ) : null}
          </div>
          {filteredOptions.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-2 py-1">No options found.</p>
          ) : (
            filteredOptions.map((opt) => (
              <label
                key={opt.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-[#1C202F] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.id)}
                  onChange={() => toggleValue(opt.id)}
                  className="h-4 w-4"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">{opt.name}</span>
              </label>
            ))
          )}
          {isLoadingMore ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 px-2 py-1">Loading more...</p>
          ) : null}
          {loadMoreError ? (
            <div className="flex items-center justify-between gap-2 px-2 py-1">
              <p className="text-xs text-red-500 dark:text-red-400">{loadMoreError}</p>
              {onRetryLoadMore ? (
                <button
                  type="button"
                  onClick={onRetryLoadMore}
                  className="text-xs text-blue-600 dark:text-blue-300 hover:underline"
                >
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {helperText ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helperText}</p> : null}
    </div>
  );
}

function validateForm(data: Record<string, any>): Record<string, string> {
  const err: Record<string, string> = {};
  if (!data.full_name?.trim()) err.full_name = "Name is required.";
  if (!data.designation?.trim()) err.designation = "Designation is required.";
  if (!data.contact?.trim()) err.contact = "Contact is required.";
  if (!data.company_name?.trim()) err.company_name = "Company name is required.";
  if (!data.department?.trim()) err.department = "Department is required.";
  if (!data.email?.trim()) err.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) err.email = "Invalid email format.";
  if (!data.password?.trim()) err.password = "Password is required.";
  if (!data.confirm_password?.trim()) err.confirm_password = "Confirm password is required.";
  else if (data.password?.trim() && data.confirm_password !== data.password) {
    err.confirm_password = "Passwords do not match.";
  }
  if (!data.company_location?.trim()) err.company_location = "Company location is required.";
  return err;
}

export default function CreateMember() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    designation: "",
    email: "",
    contact: "",
    department: "",
    password: "",
    confirm_password: "",
    company_location: "",
    isEmailEnable: false,
    isWhatsappEnable: false,
    viewer_location_details: [] as string[],
    viewer_camera_details: [] as string[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const creatorId = localStorage.getItem("auth_user_id") || "";
  const [locationOptions, setLocationOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [cameraOptions, setCameraOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [locationPage, setLocationPage] = useState(1);
  const [cameraPage, setCameraPage] = useState(1);
  const [hasMoreLocations, setHasMoreLocations] = useState(true);
  const [hasMoreCameras, setHasMoreCameras] = useState(true);
  const [locationTotal, setLocationTotal] = useState(0);
  const [cameraTotal, setCameraTotal] = useState(0);
  const [loadingMoreLocations, setLoadingMoreLocations] = useState(false);
  const [loadingMoreCameras, setLoadingMoreCameras] = useState(false);
  const [locationLoadMoreError, setLocationLoadMoreError] = useState<string | null>(null);
  const [cameraLoadMoreError, setCameraLoadMoreError] = useState<string | null>(null);
  const OPTIONS_PAGE_SIZE = 10;
  const [hasLoadedLocationOptions, setHasLoadedLocationOptions] = useState(false);
  const [hasLoadedCameraOptions, setHasLoadedCameraOptions] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const selectedLocationIds = formData.viewer_location_details;
  const isCameraSelectionDisabled = selectedLocationIds.length === 0;

  const onChange = (name: string, value: string | string[] | boolean) => {
    setFormData((p) => ({ ...p, [name]: value }));
    if (typeof value === "string" && errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const loadInitialLocations = useCallback(async () => {
    if (hasLoadedLocationOptions || isLoadingLocations) return;
    setIsLoadingLocations(true);
    setLocationLoadMoreError(null);
    try {
      const payload = await authGetRequest("/location", {
        clientId: getEffectiveClientId() || undefined,
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const list = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, 1);
      const mapped = list.map((loc: any) => ({
        id: String(loc.id ?? ""),
        name: String(loc.name ?? loc.location_name ?? "Unnamed Location"),
      }));
      setLocationOptions(Array.from(new Map(mapped.map((x) => [x.id, x])).values()));
      setLocationPage(page);
      setLocationTotal(total);
      setHasMoreLocations(mapped.length > 0 && (total > 0 ? total > page * size : mapped.length === size));
      setHasLoadedLocationOptions(true);
    } catch (err: any) {
      setLocationLoadMoreError(err?.response?.data?.message ?? "Failed to load locations.");
    } finally {
      setIsLoadingLocations(false);
    }
  }, [hasLoadedLocationOptions, isLoadingLocations]);

  const loadInitialCameras = useCallback(async () => {
    if (hasLoadedCameraOptions || isLoadingCameras) return;
    setIsLoadingCameras(true);
    setCameraLoadMoreError(null);
    try {
      const payload = await authGetRequest("/camera/search", {
        clientId: getEffectiveClientId() || undefined,
        ...(selectedLocationIds.length > 0 ? { locationIds: selectedLocationIds.join(",") } : {}),
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const list = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, 1);
      const mapped = list.map((cam: any) => ({
        id: String(cam.id ?? cam._id ?? ""),
        name: String(cam.aliasName ?? cam.name ?? "Unnamed Camera"),
      }));
      setCameraOptions(Array.from(new Map(mapped.map((x) => [x.id, x])).values()));
      setCameraPage(page);
      setCameraTotal(total);
      setHasMoreCameras(mapped.length > 0 && (total > 0 ? total > page * size : mapped.length === size));
      setHasLoadedCameraOptions(true);
    } catch (err: any) {
      setCameraLoadMoreError(err?.response?.data?.message ?? "Failed to load cameras.");
    } finally {
      setIsLoadingCameras(false);
    }
  }, [hasLoadedCameraOptions, isLoadingCameras, selectedLocationIds]);

  const loadMoreLocations = useCallback(async () => {
    if (loadingMoreLocations || !hasMoreLocations || !hasLoadedLocationOptions) return;
    const nextPage = locationPage + 1;
    setLoadingMoreLocations(true);
    setLocationLoadMoreError(null);
    try {
      const payload: any = await authGetRequest("/location", {
        clientId: getEffectiveClientId() || undefined,
        page: nextPage,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const list = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, nextPage);
      const normalizedTotal = total || locationTotal;
      const mapped = list.map((loc: any) => ({
        id: String(loc.id ?? ""),
        name: String(loc.name ?? loc.location_name ?? "Unnamed Location"),
      }));
      setLocationOptions((prev) => Array.from(new Map([...prev, ...mapped].map((x) => [x.id, x])).values()));
      setLocationPage(page);
      setLocationTotal(normalizedTotal);
      setHasMoreLocations(mapped.length > 0 && (normalizedTotal > 0 ? normalizedTotal > page * size : mapped.length === size));
    } catch (err: any) {
      setLocationLoadMoreError(err?.response?.data?.message ?? "Failed to load more locations.");
    } finally {
      setLoadingMoreLocations(false);
    }
  }, [loadingMoreLocations, hasMoreLocations, hasLoadedLocationOptions, locationPage, locationTotal]);

  const loadMoreCameras = useCallback(async () => {
    if (loadingMoreCameras || !hasMoreCameras || !hasLoadedCameraOptions) return;
    const nextPage = cameraPage + 1;
    setLoadingMoreCameras(true);
    setCameraLoadMoreError(null);
    try {
      const payload: any = await authGetRequest("/camera/search", {
        clientId: getEffectiveClientId() || undefined,
        ...(selectedLocationIds.length > 0 ? { locationIds: selectedLocationIds.join(",") } : {}),
        page: nextPage,
        pageSize: OPTIONS_PAGE_SIZE,
      });
      const list = getPagedList(payload);
      const { page, size, total } = getPagingMeta(payload, OPTIONS_PAGE_SIZE, nextPage);
      const normalizedTotal = total || cameraTotal;
      const mapped = list.map((cam: any) => ({
        id: String(cam.id ?? cam._id ?? ""),
        name: String(cam.aliasName ?? cam.name ?? "Unnamed Camera"),
      }));
      setCameraOptions((prev) => Array.from(new Map([...prev, ...mapped].map((x) => [x.id, x])).values()));
      setCameraPage(page);
      setCameraTotal(normalizedTotal);
      setHasMoreCameras(mapped.length > 0 && (normalizedTotal > 0 ? normalizedTotal > page * size : mapped.length === size));
    } catch (err: any) {
      setCameraLoadMoreError(err?.response?.data?.message ?? "Failed to load more cameras.");
    } finally {
      setLoadingMoreCameras(false);
    }
  }, [loadingMoreCameras, hasMoreCameras, hasLoadedCameraOptions, cameraPage, cameraTotal, selectedLocationIds]);

  useEffect(() => {
    setCameraOptions([]);
    setCameraPage(1);
    setCameraTotal(0);
    setHasMoreCameras(true);
    setCameraLoadMoreError(null);
    setHasLoadedCameraOptions(false);
    setFormData((prev) => ({ ...prev, viewer_camera_details: [] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationIds.join("|")]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formErrors = validateForm(formData);
      setErrors(formErrors);
      if (Object.keys(formErrors).length > 0) return;
      setIsSubmitting(true);
      try {
        await authPostRequest("/client/viewer", {
          full_name: formData.full_name,
          company_name: formData.company_name,
          company_location: formData.company_location,
          company_logo: "",
          roles: ["VIEWER"],
          email: formData.email,
          password: formData.password,
          contact: formData.contact,
          designation: formData.designation,
          department: formData.department,
          country_code: "IN",
          site_map: "",
          snapshot_domain: "",
          webrtc_domain: "",
          hls_domain: "",
          recording_domain: "",
          gpus: "",
          isEmailEnable: formData.isEmailEnable,
          isWhatsappEnable: formData.isWhatsappEnable,
          viewer_location_details: formData.viewer_location_details,
          viewer_camera_details: formData.viewer_camera_details,
          creator_id: creatorId || undefined,
        });
        toast.success("Member created successfully.");
        navigate("/member");
      } catch (err: any) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to create member.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, navigate, creatorId]
  );

  const inputClass =
    "w-full rounded-lg bg-white border border-slate-300 text-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/25 focus:border-blue-500/40 transition-colors dark:bg-[#0B0F19] dark:border-[#334155] dark:text-slate-100 dark:placeholder:text-slate-500";
  const labelClass = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1";

  return (
    <div className="space-y-5 p-5 min-h-full overflow-y-auto font-sans transition-colors duration-300 bg-white text-slate-900 dark:bg-transparent dark:text-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Create New Member</h2>
          <p className="text-slate-600 dark:text-slate-400">Add a new user account with access permissions</p>
        </div>
        <button
          onClick={() => navigate("/member")}
          className="inline-flex items-center gap-2 px-4 py-2 text-slate-700 dark:text-slate-300 bg-white dark:bg-[#1C202F] border border-slate-300 dark:border-[#334155] hover:bg-slate-100 dark:hover:bg-[#1C202F]/70 rounded-lg font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Members
        </button>
      </div>
      <div className="bg-white/80 dark:bg-[#1C202F]/55 rounded-xl border border-slate-200 dark:border-slate-700/70 overflow-hidden backdrop-blur-sm">
        <form onSubmit={onSubmit} className="p-6 space-y-6">
          <div>
            <h4 className="text-lg font-medium text-slate-900 dark:text-white my-4 flex items-center gap-2">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["full_name", "Full Name*", "text"],
                ["company_name", "Company Name*", "text"],
                ["department", "Department*", "text"],
                ["designation", "Designation*", "text"],
                ["email", "Email Address*", "email"],
                ["contact", "Contact Number*", "text"],
                ["password", "Password*", "password"],
                ["confirm_password", "Confirm Password*", "password"],
              ].map(([key, label, type]) => (
                <div key={key}>
                  <label htmlFor={key} className={labelClass}>{label}</label>
                  <input
                    id={key}
                    type={type}
                    value={(formData as any)[key]}
                    onChange={(e) => onChange(key, e.target.value)}
                    className={`${inputClass} ${(errors as any)[key] ? "border-red-500" : ""}`}
                  />
                  {(errors as any)[key] ? (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">{(errors as any)[key]}</p>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="mt-4">
                <label htmlFor="company_location" className={labelClass}>Company Location*</label>
                <textarea
                  id="company_location"
                  rows={3}
                  value={formData.company_location}
                  onChange={(e) => onChange("company_location", e.target.value)}
                  className={`${inputClass} resize-none ${errors.company_location ? "border-red-500" : ""}`}
                />
                {errors.company_location ? (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors.company_location}</p>
                ) : null}
              </div>
          </div>
          <div>
            <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-4">Notification Alerts</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={formData.isEmailEnable} onChange={(e) => onChange("isEmailEnable", e.target.checked)} className="h-4 w-4" />
                <span>Email alerts</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={formData.isWhatsappEnable} onChange={(e) => onChange("isWhatsappEnable", e.target.checked)} className="h-4 w-4" />
                <span>WhatsApp alerts</span>
              </label>
            </div>
          </div>
          <div>
            <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              Assign Locations & Cameras
            </h4>
            <div className="grid grid-cols-1 space-y-4">
              <CheckboxMultiSelect
                id="viewer_location_details"
                label="Allowed Locations"
                options={locationOptions}
                selectedValues={formData.viewer_location_details}
                onChange={(next) => onChange("viewer_location_details", next)}
                onOpen={loadInitialLocations}
                onLoadMore={loadMoreLocations}
                hasMore={hasMoreLocations}
                isLoadingMore={loadingMoreLocations || isLoadingLocations}
                loadMoreError={locationLoadMoreError}
                onRetryLoadMore={hasLoadedLocationOptions ? loadMoreLocations : loadInitialLocations}
                totalCount={locationTotal}
                helperText="Choose one or more locations for this member."
              />
              <CheckboxMultiSelect
                id="viewer_camera_details"
                label="Allowed Cameras"
                options={cameraOptions}
                selectedValues={formData.viewer_camera_details}
                onChange={(next) => onChange("viewer_camera_details", next)}
                onOpen={loadInitialCameras}
                onLoadMore={loadMoreCameras}
                hasMore={hasMoreCameras}
                isLoadingMore={loadingMoreCameras || isLoadingCameras}
                loadMoreError={cameraLoadMoreError}
                onRetryLoadMore={hasLoadedCameraOptions ? loadMoreCameras : loadInitialCameras}
                totalCount={cameraTotal}
                disabled={isCameraSelectionDisabled}
                helperText={isCameraSelectionDisabled ? "Select at least one location to choose cameras." : "Selected member should only access these cameras."}
              />
             
            </div>
          </div>
          <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-slate-700">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Create Member
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
